import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAnnouncements, createAnnouncement, deleteAnnouncement } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, MegaphoneIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';

const CATEGORIES = ['General', 'Deadline', 'Opportunity', 'Fellowship Notice', 'Conference', 'Scholarship'];

const CAT_COLORS = {
  'General': 'bg-gray-800 text-gray-300',
  'Deadline': 'bg-red-900/50 text-red-300',
  'Opportunity': 'bg-green-900/50 text-green-300',
  'Fellowship Notice': 'bg-blue-900/50 text-blue-300',
  'Conference': 'bg-purple-900/50 text-purple-300',
  'Scholarship': 'bg-amber-900/50 text-amber-300',
};

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-lg">{children}</div>
    </div>
  );
}

export default function AnnouncementsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', category: 'General', is_pinned: false });

  const { data, isLoading } = useQuery({ queryKey: ['announcements'], queryFn: () => getAnnouncements() });

  const createMut = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => { qc.invalidateQueries(['announcements']); toast.success('Announcement posted!'); setShowModal(false); setForm({ title: '', body: '', category: 'General', is_pinned: false }); },
    onError: () => toast.error('Failed to post announcement'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => { qc.invalidateQueries(['announcements']); toast.success('Deleted'); },
  });

  const announcements = data?.data || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Announcements</h1>
          <p className="text-gray-500 text-sm">Updates and notices for all fellows</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> Post Announcement
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : announcements.length === 0 ? (
        <div className="card text-center py-12">
          <MegaphoneIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No announcements yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => (
            <div key={a.id} className={`card ${a.is_pinned ? 'border-amber-700/50' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {a.is_pinned && <StarIcon className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" />}
                    <span className="font-semibold text-white">{a.title}</span>
                    <span className={`badge ${CAT_COLORS[a.category] || 'bg-gray-800 text-gray-300'}`}>{a.category}</span>
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{a.body}</p>
                  <div className="text-xs text-gray-600 mt-3">
                    Posted by {a.author?.full_name} · {format(new Date(a.created_at), 'dd MMM yyyy')}
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={() => { if (window.confirm('Delete announcement?')) deleteMut.mutate(a.id); }} className="text-red-400 hover:text-red-300 flex-shrink-0">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Post Announcement</h2>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(form); }} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Category</label>
                <select className="input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-500 block mb-1">Body *</label>
                <textarea className="input resize-none" rows={5} required value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} /></div>
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm(f => ({ ...f, is_pinned: e.target.checked }))} />
                Pin this announcement
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>
                  {createMut.isPending ? 'Posting...' : 'Post'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
